import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

/**
 * StudioDock — the floating bottom action bar for the 3D studios.
 * A blurred bar of icon buttons; clicking one opens a compact panel
 * ABOVE the bar with that tool's options (pills / sliders). One panel at a time;
 * clicking anywhere outside the dock closes it. The `action` slot (right end,
 * after a divider) hosts the primary Capture button.
 */

export interface StudioDockItemDef {
    key: string;
    /** Tool name — used as the accessible label + fallback caption. */
    label: string;
    /** Live value caption shown under the icon (e.g. "35mm", "4:5"). */
    value?: string;
    icon: React.ReactNode;
    panel: React.ReactNode;
    panelWidth?: number;
}

export const StudioDock: React.FC<{ items: StudioDockItemDef[]; action?: React.ReactNode; initialOpenKey?: string }> = ({ items, action, initialOpenKey }) => {
    const [openKey, setOpenKey] = useState<string | null>(initialOpenKey || null);
    const rootRef = useRef<HTMLDivElement | null>(null);

    // Close the open panel on any pointer-down outside the dock. Capture phase so
    // the viewport's stopPropagation handlers can't swallow the event first.
    useEffect(() => {
        if (!openKey) return;
        const onDown = (e: PointerEvent) => {
            if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpenKey(null);
        };
        window.addEventListener('pointerdown', onDown, true);
        return () => window.removeEventListener('pointerdown', onDown, true);
    }, [openKey]);

    const open = items.find((i) => i.key === openKey);

    return (
        <div
            ref={rootRef}
            className="absolute bottom-3.5 left-1/2 z-[6] max-w-[calc(100%-24px)] -translate-x-1/2"
        >
            {/* Options panel — floats above the bar, centred. */}
            {open && (
                <div
                    className="absolute bottom-[calc(100%+10px)] left-1/2 max-h-[340px] max-w-[calc(100vw-32px)] -translate-x-1/2 overflow-y-auto rounded-xl border bg-background/95 p-3.5 backdrop-blur-md"
                    style={{ width: open.panelWidth ?? 300 }}
                >
                    {open.panel}
                </div>
            )}

            {/* The bar + the separate action button beside it. */}
            <div className="flex items-center gap-2">
                <div className="flex items-center gap-0.5 rounded-md border bg-background/90 px-1.5 py-1.5 backdrop-blur-md">
                    {items.map((item) => {
                        const active = openKey === item.key;
                        return (
                            <Button
                                key={item.key}
                                variant="ghost"
                                onClick={() => setOpenKey((k) => (k === item.key ? null : item.key))}
                                title={item.label}
                                aria-label={item.label}
                                aria-pressed={active}
                                className={cn(
                                    'h-auto min-w-[58px] flex-col gap-0.5 px-2 py-1.5 [&_svg]:size-[19px]',
                                    active ? 'bg-muted text-foreground' : 'text-muted-foreground',
                                )}
                            >
                                <span className="flex">{item.icon}</span>
                                <span className="max-w-[84px] truncate text-[10px] leading-tight font-medium">
                                    {item.value ?? item.label}
                                </span>
                            </Button>
                        );
                    })}
                </div>
                {/* Primary action — a standalone control next to the dock, not part of it. */}
                {action}
            </div>
        </div>
    );
};
